/**
 * Types pour la vue intérieure 360° (cubemap)
 * Basé sur l'analyse de InteriorView.js et InteriorView2.js
 */

/** Configuration de la caméra intérieure */
export interface InteriorCameraConfig {
  /** Champ de vision (Field of View) en degrés */
  fov: number;
  /** Position de la caméra dans l'espace 3D */
  position: {
    x: number;
    y: number;
    z: number;
  };
  /** Distance minimale de rendu */
  near: number;
  /** Distance maximale de rendu */
  far: number;
}

/** Configuration des contrôles orbitaux */
export interface InteriorControlsConfig {
  /** Activer/désactiver le zoom */
  enableZoom: boolean;
  /** Activer/désactiver le pan (déplacement latéral) */
  enablePan: boolean;
  /** Activer/désactiver le damping (inertie) */
  enableDamping: boolean;
  /** Facteur de damping (0-1) */
  dampingFactor: number;
  /** Angle polaire minimal (en radians) */
  minPolarAngle: number;
  /** Angle polaire maximal (en radians) */
  maxPolarAngle: number;
  /** Vitesse de rotation */
  rotateSpeed: number;
}

/** URLs des 6 faces du cubemap */
export interface CubeMapFaces {
  /** Face droite (+X) */
  right: string;
  /** Face gauche (-X) */
  left: string;
  /** Face haut (+Y) */
  top: string;
  /** Face bas (-Y) - rotation 180° appliquée */
  bottom: string;
  /** Face avant (+Z) */
  front: string;
  /** Face arrière (-Z) */
  back: string;
}

/** Options du composable useInteriorScene */
export interface UseInteriorSceneOptions {
  /** Configuration partielle de la caméra (merge avec défaut) */
  camera?: Partial<InteriorCameraConfig>;
  /** Configuration partielle des contrôles (merge avec défaut) */
  controls?: Partial<InteriorControlsConfig>;
  /** Démarrer l'animation automatiquement (défaut: true) */
  autoAnimate?: boolean;
  /** FPS en mode throttlé quand inactif (défaut: 5) */
  throttledFps?: number;
}

/** Valeurs par défaut pour la configuration de la caméra */
export const DEFAULT_CAMERA_CONFIG: InteriorCameraConfig = {
  fov: 55.3,
  position: { x: 100, y: 20, z: 0 }, // orienté vers le tableau de bord
  near: 1,
  far: 5000
}

/** Valeurs par défaut pour la configuration des contrôles */
export const DEFAULT_CONTROLS_CONFIG: InteriorControlsConfig = {
  enableZoom: false,
  enablePan: false,
  enableDamping: true,
  dampingFactor: 0.25,
  minPolarAngle: 0.47, // ~27°
  maxPolarAngle: 2.67, // ~153°
  rotateSpeed: -0.05
}